/**
 * 기관 서식 레이아웃 정규화.
 * printLayout 상수·clamp만 재사용. gridId prefs 저장은 쓰지 않음.
 * SSOT: D:\295\erp\CM\design\06_출력서식_카탈로그.md §8
 */
import {
  PRINT_ALIGN_OPTIONS,
  PRINT_COL_WIDTH_MAX,
  PRINT_COL_WIDTH_MIN,
  applyPrintTokens,
  clampColMm,
  defaultPrintLayout,
  normalizePrintLayout,
} from 'components/grid/print/printLayout';
import { buildPrintHtml } from 'components/grid/print/printHtml';
import { formatPrintCell } from 'components/grid/print/printModel';
import { buildBodyMergeMap } from 'components/grid/hiGrid/core/bodyMergeUtils';

export {
  PRINT_ALIGN_OPTIONS,
  PRINT_BAND_FONT_SIZE_OPTIONS,
  PRINT_COL_WIDTH_MAX,
  PRINT_COL_WIDTH_MIN,
  PRINT_FONT_OPTIONS,
  PRINT_FONT_SIZE_OPTIONS,
  PRINT_ORIENT_OPTIONS,
  PRINT_PAPER_OPTIONS,
  PRINT_STAMP_BUILTIN,
  PRINT_STAMP_COUNTS,
  PRINT_STAMP_POSITIONS,
  PRINT_TOKENS,
  defaultStamp,
  todayYmd,
} from 'components/grid/print/printLayout';

export const REPORT_LAYOUT_TOKENS = [
  { token: '{출력일}', label: '출력일' },
  { token: '{페이지}', label: '페이지' },
  { token: '{전체쪽}', label: '전체쪽' },
  { token: '{문서명}', label: '문서명' },
  { token: '{기관}', label: '기관' },
  { token: '{사용자}', label: '사용자' },
  { token: '{회계연도}', label: '회계연도' },
];

export function defaultReportLayout({ title = '' } = {}) {
  return normalizePrintLayout(defaultPrintLayout({ title, columnIds: [] }), {
    title,
    columnIds: [],
  });
}

export function normalizeReportLayout(raw, { title = '' } = {}) {
  return normalizePrintLayout(raw, { title: raw?.title || title, columnIds: [] });
}

export function applyReportTokens(text, ctx = {}) {
  let s = applyPrintTokens(text, {
    ...ctx,
    screenName: ctx.docName || ctx.screenName || '',
  });
  const extra = {
    '{문서명}': ctx.docName || ctx.screenName || '',
    '{회계연도}': ctx.fiscalYear != null ? String(ctx.fiscalYear) : '',
  };
  Object.entries(extra).forEach(([token, value]) => {
    s = s.split(token).join(value);
  });
  return s;
}

function mapBandCell(cell, ctx) {
  if (!cell || typeof cell !== 'object') return cell;
  return { ...cell, text: applyReportTokens(cell.text, ctx) };
}

function mapBand(band, ctx) {
  if (!band) return band;
  return {
    left: mapBandCell(band.left, ctx),
    center: mapBandCell(band.center, ctx),
    right: mapBandCell(band.right, ctx),
  };
}

/** buildPrintHtml 전에 카탈로그 전용 토큰을 풀어 둔다 */
export function materializeReportLayout(layout, ctx = {}) {
  const n = normalizeReportLayout(layout, { title: layout?.title });
  return {
    ...n,
    title: applyReportTokens(n.title, ctx),
    header: mapBand(n.header, ctx),
    footer: mapBand(n.footer, ctx),
  };
}

export function mappableDatasetFields(dataset) {
  return (dataset?.fields || []).filter((f) => f.field && f.field !== 'requestUid');
}

export function defaultFieldMap(dataset) {
  const fields = mappableDatasetFields(dataset);
  let order = 0;
  return fields.map((f) => {
    order += 10;
    return {
      field: f.field,
      label: f.label,
      widthMm: clampColMm(f.widthMm, 20),
      align: PRINT_ALIGN_OPTIONS.some((o) => o.value === f.align) ? f.align : 'left',
      visible: f.printDefault !== false,
      order,
    };
  });
}

export function normalizeFieldMap(raw, dataset) {
  const defs = mappableDatasetFields(dataset);
  const byField = new Map((Array.isArray(raw) ? raw : []).map((r) => [String(r.field || ''), r]));
  let order = 0;
  return defs.map((f) => {
    const cur = byField.get(f.field) || {};
    order += 10;
    const w = Number(cur.widthMm);
    return {
      field: f.field,
      label: cur.label != null && String(cur.label).trim() ? String(cur.label) : f.label,
      widthMm: Number.isFinite(w)
        ? Math.max(PRINT_COL_WIDTH_MIN, Math.min(PRINT_COL_WIDTH_MAX, Math.round(w)))
        : clampColMm(f.widthMm, 20),
      align: PRINT_ALIGN_OPTIONS.some((o) => o.value === cur.align) ? cur.align : f.align || 'left',
      visible: Object.prototype.hasOwnProperty.call(cur, 'visible')
        ? cur.visible !== false
        : f.printDefault !== false,
      order: Number.isFinite(Number(cur.order)) ? Number(cur.order) : order,
    };
  }).sort((a, b) => a.order - b.order || a.field.localeCompare(b.field))
    .map((row, i) => ({ ...row, order: (i + 1) * 10 }));
}

function visibleColumns(template, dataset) {
  const defs = new Map((dataset?.fields || []).map((f) => [f.field, f]));
  return [...(template?.fieldMap || [])]
    .filter((f) => f.visible !== false)
    .sort((a, b) => a.order - b.order)
    .map((f) => {
      const def = defs.get(f.field);
      return {
        field: f.field,
        header: f.label || def?.label || f.field,
        type: def?.type || 'text',
        align: f.align || def?.align || 'left',
        mergeRows: def?.mergeYn === true,
        mergeGroupKey: dataset?.mergeGroupKey || null,
      };
    });
}

export function buildReportPreviewModel(template, dataset, sampleRows = []) {
  const columns = visibleColumns(template, dataset);
  const rows = (sampleRows || []).map((original, index) => ({
    index,
    original: original || {},
    cells: Object.fromEntries(
      columns.map((c) => [c.field, formatPrintCell(original?.[c.field], c)])
    ),
  }));
  const mergeCols = columns
    .filter((c) => c.mergeRows)
    .map((c) => ({
      id: c.field,
      accessorKey: c.field,
      meta: { mergeRows: true, mergeGroupKey: c.mergeGroupKey },
    }));
  const mergeMap = buildBodyMergeMap(
    rows.map((r) => ({ index: r.index, original: r.original })),
    mergeCols
  );
  return {
    gridId: '',
    gridLabel: template?.displayName || template?.layout?.title || '',
    columns,
    rows,
    mergeMap,
    rowCount: rows.length,
  };
}

export function buildReportPreviewHtml(template, dataset, sampleRows, ctx = {}) {
  const layout = materializeReportLayout(template?.layout, ctx);
  const widths = {};
  (template?.fieldMap || []).forEach((f) => {
    if (f.visible === false) return;
    widths[f.field] = f.widthMm;
  });
  layout.columnWidthsMm = { ...layout.columnWidthsMm, ...widths };
  const model = buildReportPreviewModel(template, dataset, sampleRows);
  return buildPrintHtml(model, layout, {
    ...ctx,
    screenName: ctx.docName || layout.title || '',
    tenantName: ctx.tenantName || ctx.tenantCode || '',
  });
}
